import React from 'react';
import { Link } from 'react-router-dom';
import { Languages, ArrowLeft, Globe, Code, BookOpen } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
              <Languages className="w-5 h-5 text-primary" />
            </div>
            <span className="font-heading text-xl font-semibold text-foreground">
              <span className="text-primary/70">Language</span><span className="text-primary">Constructor</span>
            </span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-foreground-muted hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
              About LanguageConstructor
            </h1>
            <p className="text-foreground-muted text-lg">
              A platform for creating and documenting constructed languages
            </p>
          </div>

          {/* Purpose Section */}
          <section className="mb-12">
            <div className="bg-background-paper border border-border rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-primary/20 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  Our Purpose
                </h2>
              </div>
              <p className="text-foreground-muted leading-relaxed text-lg">
                LanguageConstructor is born of a desire to share the beauty of language 
                and the joy of exploring the amazing complexity of the human language facility.
              </p>
              <p className="text-foreground-muted leading-relaxed mt-4">
                Whether you're a writer building languages for fictional worlds, a linguist 
                exploring language structure, or simply someone fascinated by how language works, 
                LanguageConstructor provides the tools you need to bring your linguistic 
                creations to life.
              </p>
            </div>
          </section>

          {/* Creator Section */}
          <section className="mb-12">
            <div className="bg-background-paper border border-border rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-secondary/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-secondary" />
                </div>
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  The Creator
                </h2>
              </div>
              <p className="text-foreground-muted leading-relaxed text-lg">
                <span className="text-foreground font-medium">Gene Rohrbaugh</span> is 
                a linguist and computer scientist with a lifelong love of languages of 
                all kinds — natural human languages, constructed languages, and even 
                computer languages.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm">
                  <Globe className="w-4 h-4" />
                  Linguist
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm">
                  <Code className="w-4 h-4" />
                  Computer Scientist
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-500/10 text-green-500 rounded-full text-sm">
                  <Languages className="w-4 h-4" />
                  Language Enthusiast
                </span>
              </div>
            </div>
          </section>

          {/* Features Summary */}
          <section>
            <div className="bg-background-paper border border-border rounded-lg p-8">
              <h2 className="font-heading text-2xl font-semibold text-foreground mb-6">
                What You Can Do
              </h2>
              <ul className="space-y-4 text-foreground-muted">
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Define phonology with interactive IPA charts for consonants and vowels</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Build your lexicon with a word generator based on your phonotactics</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Create morphology rules including affixes and inflection patterns</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Define syntax including word order, clause structures, and sentence types</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Write sample texts with translations to showcase your language</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-primary mt-1">•</span>
                  <span>Share your creations with friends or the public community</span>
                </li>
              </ul>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link 
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors"
            >
              Start Creating
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-foreground-muted text-sm">
          <p>© {new Date().getFullYear()} LanguageConstructor. Built with love for language.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
